import { MdPieChartOutline } from 'react-icons/md';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { AgentApiAnalysisSalesBreakdownRow } from '@/types/serializers';
import InsightEmptyState from '../InsightEmptyState';
import SalesRegisterButton from './SalesRegisterButton';
import { CHART_FOCUS_RESET, formatYen, trendColorForIndex } from './salesChartUtils';

interface Props {
  title: string;
  rows: AgentApiAnalysisSalesBreakdownRow[];
}

type TooltipPayloadItem = { name?: string; value?: number | string; payload?: { name?: string } };
type TooltipProps = { active?: boolean; payload?: TooltipPayloadItem[] };

function DonutTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const item = payload[0];
  return (
    <div className="flex items-center gap-sm rounded-sm bg-white px-sm py-xs text-body-xs whitespace-nowrap shadow-layer2">
      <span className="text-main">{item.payload?.name ?? item.name}</span>
      <span className="font-bold text-main">{formatYen(Number(item.value) || 0)}</span>
    </div>
  );
}

/**
 * グループ別売上内訳のドーナツチャート（期間内 担当者別内訳 / ポジション別 売上内訳で共通）。
 * 中央に合計金額を表示し、凡例にグループ名・構成比を表示する。
 * ホバー時はカーソルに追従してチップを表示する。
 */
export default function SalesDonutChart({ title, rows }: Props) {
  const total = rows.reduce((sum, r) => sum + r.value, 0);
  const hasData = total > 0;

  return (
    <div className={`w-full ${CHART_FOCUS_RESET}`}>
      <div className="flex items-start justify-between gap-md pb-sm">
        <h3 className="m-0 text-heading-xs text-main">{title}</h3>
        {!hasData && <SalesRegisterButton />}
      </div>

      {hasData ? (
        <div className="flex flex-col items-center gap-md">
          <div className="relative h-[220px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Tooltip
                  content={<DonutTooltip />}
                  wrapperStyle={{ zIndex: 10, outline: 'none' }}
                  allowEscapeViewBox={{ x: true, y: true }}
                />
                <Pie
                  data={rows}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={0}
                  stroke="none"
                  isAnimationActive={false}
                >
                  {rows.map((row, idx) => (
                    <Cell key={row.key} fill={trendColorForIndex(idx)} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-body-xs text-sub">合計</span>
              <span className="text-heading-sm text-main">{formatYen(total)}</span>
            </div>
          </div>

          <div className="flex w-full flex-wrap justify-center gap-x-lg gap-y-sm">
            {rows.map((row, idx) => {
              const ratio = total === 0 ? 0 : Math.round((row.value / total) * 100);
              const color = trendColorForIndex(idx);
              return (
                <div key={row.key} className="flex min-w-0 flex-col items-center gap-xxs">
                  <div className="flex min-w-0 items-center justify-center gap-xs">
                    <span
                      className="inline-block size-3 shrink-0 rounded-xs"
                      style={{ backgroundColor: color }}
                    />
                    <span className="truncate text-body-xs text-sub" title={row.name}>
                      {row.name}
                    </span>
                  </div>
                  <span className="text-center text-heading-xs font-bold" style={{ color }}>
                    {ratio}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <InsightEmptyState
          icon={MdPieChartOutline}
          description="データを登録すると、ここにグラフが表示されます"
        />
      )}
    </div>
  );
}
